import { confirmSignIn, signIn, signOut } from "aws-amplify/auth";
import { useQueryClient } from "@tanstack/react-query";
import { FormState, useAuthStore } from "@/stores/authStore";
import { UseFormReturn } from "react-hook-form";
import { ApiError, DefaultService } from "@/api/SolarCloudApi";
import { toast } from "sonner";

export function useAuth() {
  const queryClient = useQueryClient();
  const { setFormState, setData } = useAuthStore();

  const handleSignIn = async (
    email: string,
    password: string,
    form: UseFormReturn<any>,
    handleVerified: () => void
  ) => {
    setData({ email, password });

    try {
      const { nextStep } = await signIn({ username: email, password });

      console.log("next step: ", nextStep.signInStep);

      if (
        nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        return nextStep.signInStep;
      } else if (nextStep.signInStep === "RESET_PASSWORD") {
        return nextStep.signInStep;
      } else if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        handleSendVerification(email, false);
        setFormState(FormState.VERIFICATION);
      } else {
        await queryClient.invalidateQueries({ queryKey: ["authentication"] });
        handleVerified();
      }
      // if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {

      // }
      // if (nextStep.signInStep === 'DONE') {

      // }
    } catch (error: any) {
      console.log("error signing in: ", error);
      const errorType = error.code || error.name || error.__type;
      if (errorType === "UsernameExistsException") {
        form.setError("email", {
          type: "manual",
          message: "Email already exists",
        });
      } else if (errorType === "UserNotConfirmedException") {
        setData({ email, password });
        setFormState(FormState.VERIFICATION);
      } else if (errorType === "NotAuthorizedException") {
        form.setError("email", {
          type: "manual",
          message: "Incorrect email or password",
        });
      } else {
        form.setError("email", {
          type: "manual",
          message: "Something went wrong",
        });
      }
    }
  };

  const handleConfirmNewPassword = async (
    password: string,
    form: UseFormReturn<any>,
    handleVerified: () => void
  ) => {
    try {
      await confirmSignIn({
        challengeResponse: password,
      });
      await queryClient.invalidateQueries({ queryKey: ["authentication"] });
      handleVerified();
    } catch (error) {
      form.setError("email", {
        type: "manual",
        message: "Something went wrong",
      });
    }
  };

  const signInAndRedirect = async (email: string, password: string) => {
    setData({ email, password });

    try {
      await signIn({ username: email, password });
      await queryClient.invalidateQueries({ queryKey: ["authentication"] });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.code || "Unknown error",
      };
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      await queryClient.invalidateQueries({ queryKey: ["authentication"] });
      queryClient.clear();
    } catch (error) {
      console.log("error signing out: ", error);
    }
  };

  const handleSendVerification = async (email: string, resent = true) => {
    try {
      await DefaultService.verificationCode(email);
      toast.success(
        `Verification code ${resent ? "resent" : "sent"} to your email`
      );
      return { success: true };
    } catch (error: ApiError | any) {
      const code = error?.body?.code || error?.code;
      if (code === "ALREADY_VERIFIED") {
        toast("Email already verified");
        setFormState(FormState.SIGNIN);
        return { success: false, alreadyVerified: true };
      } else {
        toast(`Error ${resent ? "resending" : "sending"} verification code`);
        console.error(
          `Error ${resent ? "resending" : "sending"} verification ${code} error`
        );
        return { success: false };
      }
    }
  };

  return {
    handleSignIn,
    handleConfirmNewPassword,
    signInAndRedirect,
    handleSignOut,
    handleSendVerification,
  };
}
